import React, { lazy, ReactNode, Suspense, useEffect, useRef, useState } from 'react';
import { ReactSVG } from 'react-svg';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useTheme } from '@forma/forma-ui-theme';
import { Button, Input, LoadingButton, Modal, PhoneInput, phonemasks } from '@forma/forma-ui-kit';
import { useRequestCall } from '@/components/blocks/RequestCall';

import styles from './contact.module.css';

const QRCode = lazy(() => import('react-qr-code'));

const tgUrl = process.env.TG_CONTACT_URL ?? '';
const maxUrl = process.env.MAX_URL ?? '';

const CallPopup = () => {
  const { t } = useTranslation();
  const { lang } = useTheme();
  const [phoneValidation, setPhoneValidation] = useState('');

  const { onSubmit, isSuccess, isError, isLoading } = useRequestCall({ lang });
  const { register, handleSubmit, formState: { errors, isValid } } = useForm<FormValues>({
    mode: 'onChange'
  });

  const handleChangeMask = ({ mask, code }: { mask: string; code: string }) => {
    setPhoneValidation(code + mask);
  };

  return (
    <>
      {isSuccess && (
        <div className={styles.popupDescription}>Благодарим за обращение. Мы скоро свяжемся с вами.</div>
      )}
      {isError && (
        <div className={styles.popupDescription}>Ошибка отправки. Попробуйте позже.</div>
      )}
      {!isSuccess && !isError && (
        <form className={styles.popupForm} onSubmit={handleSubmit(onSubmit)}>
          <PhoneInput
            id="phone"
            viewStyle="secondary"
            masks={phonemasks}
            defaultMaskCode={lang === 'en' ? 'US' : lang.toUpperCase()}
            onChangeMask={handleChangeMask}
            placeholder="+7"
            error={!!errors.clientPhone}
            errorMessage={errors.clientPhone?.message}
            {...register('clientPhone', {
              required: t('please_fill_the_field'),
              minLength: {
                value: phoneValidation.length,
                message: t('errors.wrong_phone'),
              },
            })}
            autoComplete="off"
            containerClass={styles.inputContainer}
            className={styles.phoneInputInput}
            label={t('phone_number')}
            meta={{
              size: 'm'
            }}
          />
          <Input
            id="name"
            viewStyle="secondary"
            placeholder="Иван"
            {...register('name', {
              required: t('please_fill_the_field'),
              minLength: { value: 3, message: t('errors.min_lenght_3') },
              pattern: {
                value: /^[^0-9]+$/,
                message: t('errors.name_has_numbers'),
              },
            })}
            meta={{
              label: t('name'),
              error: !!errors.name,
              errorMessage: errors.name?.message,
              containerClass: styles.inputContainer,
              size: 'm'
            }}
          />

          <LoadingButton
            type="submit"
            size="m"
            disabled={!isValid}
            isLoading={isLoading}
            fullWidth
          >
            Перезвонить мне
          </LoadingButton>
        </form>
      )}
    </>
  );
};

interface ContactPopupProps {
  name: string;
  href: string;
  icon: string;
}

interface FormValues {
  name: string;
  clientPhone: string;
}

const ContactPopup = ({ name, href, icon }: ContactPopupProps) => {
  return (
    <>
      <Button
        as="a"
        href={href}
        className={styles.popupButton}
        icon={<ReactSVG src={icon} wrapper="span" />}
        size="m"
        fullWidth
      >
        Перейти в {name}
      </Button>
      <div className={styles.popupDescription}>
        Нет приложения на компьютере? Просто наведите камеру телефона на QR-код.
      </div>
      <Suspense>
        <QRCode value={href} width={180} height={180} />
      </Suspense>
    </>
  );
};

interface PopupProps {
  type: 'popup' | 'modal';
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
}

const Popup = ({ type, open, onClose, title, children }: PopupProps) => {
  if (type === 'modal') return (
    <Modal open={open} onClose={onClose} title={title} contentClassName={styles.modal}>
      {children}
    </Modal>
  );

  if (!open) return null;

  return (
    <div className={styles.popup}>
      <div className={styles.popupTitle}>
        {title}
      </div>

      {children}
    </div>
  );
};

interface ContactItemProps {
  viewStyle?: 'primary' | 'secondary';
  icon: string;
  text: ReactNode;
  className?: string;
  onClick?: () => void;
}

const ContactItem = ({ viewStyle, icon, text, className, onClick }: ContactItemProps) => {
  return (
    <div className={styles.item}>
      <div className={styles.itemLabel}>{text}</div>
      <Button
        className={classNames(styles.itemButton, className)}
        viewStyle={viewStyle}
        size="m"
        onClick={onClick}
      >
        <ReactSVG src={icon} wrapper="span" className={styles.itemIcon} />
      </Button>
    </div>
  );
};

const Contact = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { viewport } = useTheme();

  const [isOpen, setOpen] = useState(false);
  const [isAnimated, setAnimated] = useState(false);
  const [openedModal, setOpenedModal] = useState<string | null>(null);

  const closeAll = () => {
    setOpenedModal(null);
    setOpen(false);
    setAnimated(false);
  };

  const openContacts = () => {
    setOpen(true);
    setTimeout(() => setAnimated(true), 0);
  };

  const isPhone = viewport === 'phone';

  // todo: заменить на useClickOutside из "@forma/forma-ui-kit": "^2.0.16"
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as HTMLElement;
      if (ref.current && !ref.current.contains(target) && !target?.closest('#popup-root') && !target?.closest('.input-phone-codes')) {
        if (!isPhone) closeAll();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line
  }, [ref, isPhone]);

  const popupType = isPhone ? 'modal' : 'popup';

  return (
    <div className={styles.root} ref={ref}>
      {isPhone ? (
        <Modal
          open={isOpen}
          onClose={closeAll}
          contentClassName={styles.modalButtons}
        >
          <div />
          <Button
            viewStyle="secondary"
            icon={<ReactSVG src="/icons/phone.svg" wrapper="span" className={styles.icon} />}
            onClick={() => setOpenedModal('phone')}
            size="m"
            fullWidth
          >
            Позвонить мне
          </Button>
          <Button
            icon={<ReactSVG src="/icons/social/max.svg" wrapper="span" className={styles.icon} />}
            className={styles.max}
            onClick={() => setOpenedModal('max')}
            size="m"
            fullWidth
          >
            Написать в Max
          </Button>
          <Button
            icon={<ReactSVG src="/icons/social/telegram.svg" wrapper="span" className={styles.icon} />}
            className={styles.tg}
            onClick={() => setOpenedModal('tg')}
            size="m"
            fullWidth
          >
            Написать в Telegram
          </Button>
        </Modal>
      ) : (
        !openedModal && (
          <div className={classNames(styles.items, isOpen && styles.open, isAnimated && styles.animated)}>
            <ContactItem
              icon="/icons/phone.svg"
              // href={`tel:${phone.replaceAll(/[^0-9]/g, '')}`}
              text="Позвонить мне"
              viewStyle="secondary"
              onClick={() => setOpenedModal('phone')}
            />
            <ContactItem
              icon="/icons/social/max.svg"
              // href={maxUrl}
              text="Написать в Max"
              className={styles.max}
              onClick={() => setOpenedModal('max')}
            />
            <ContactItem
              icon="/icons/social/telegram.svg"
              // href={tgUrl}
              text="Написать в Telegram"
              className={styles.tg}
              onClick={() => setOpenedModal('tg')}
            />
          </div>
        )
      )}

      <Popup
        type={popupType}
        open={openedModal === 'phone'}
        onClose={closeAll}
        title="Перезвоним вам в ближайшие 5 минут и ответим на все вопросы"
      >
        <CallPopup />
      </Popup>
      <Popup
        type={popupType}
        open={openedModal === 'max'}
        onClose={closeAll}
        title="Нажмите, чтобы открыть чат"
      >
        <ContactPopup
          name="Max"
          href={maxUrl}
          icon="/icons/social/max.svg"
        />
      </Popup>
      <Popup
        type={popupType}
        open={openedModal === 'tg'}
        onClose={closeAll}
        title="Нажмите, чтобы открыть чат"
      >
        <ContactPopup
          name="Telegram"
          href={tgUrl}
          icon="/icons/social/telegram.svg"
        />
      </Popup>

      <Button
        className={styles.button}
        onClick={() => isOpen ? closeAll() : openContacts()}
      >
        <ReactSVG src="/icons/message-text-filled.svg" wrapper="span" className={styles.icon} />
      </Button>
    </div>
  );
};

export default Contact;
